import { Link, useLocation } from "@tanstack/react-router";

import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { cars } from "@/lib/cars";


type NavItem = {
  to: "/" | "/fleet" | "/rates" | "/delivery" | "/faq" | "/contact" | "/partners" | "/about" | "/blog";
  label: string;
  hasDropdown?: boolean;
  isServices?: boolean;
  isHome?: boolean;
  hideOnDesktop?: boolean;
};
const services = [
  { to: "/luxury-car-rental-vancouver", label: "Luxury Car Rental Vancouver" },
  { to: "/exotic-car-rental-vancouver", label: "Exotic Car Rental Vancouver" },
  { to: "/wedding-car-rental-vancouver", label: "Wedding Car Rental Vancouver" },
  { to: "/yvr-airport-car-rental", label: "YVR Airport Car Rental" },
] as const;
const nav: NavItem[] = [
  { to: "/", label: "Home", hasDropdown: true, isHome: true },
  { to: "/fleet", label: "Fleet", hasDropdown: true },
  { to: "/rates", label: "Rates" },
  { to: "/delivery", label: "Delivery", hasDropdown: true, isServices: true },
  { to: "/partners", label: "Partners" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact", hideOnDesktop: true },
];

const announcements = [
  'USE CODE "FALL10" FOR 10% OFF',
  "FREE DELIVERY IN VANCOUVER & YVR AIRPORT",
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [annIndex, setAnnIndex] = useState(0);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const visibleNav = nav;
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const id = setInterval(
      () => setAnnIndex((i) => (i + 1) % announcements.length),
      4000,
    );
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
  }, [pathname]);


  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);



  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <div
        className="bg-foreground text-background text-center text-[10px] md:text-[11px] uppercase tracking-[0.25em] px-4 font-medium relative h-[38px]"
        aria-live="polite"
      >
        {announcements.map((text, i) => (
          <span
            key={text}
            aria-hidden={i !== annIndex}
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
              i === annIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            {text}
          </span>
        ))}
      </div>

      <header
        className={`transition-all duration-500 ${
        scrolled
          ? "bg-background/90 backdrop-blur-md border-b border-border text-foreground"
          : transparent
            ? "bg-transparent border-b border-white/10 text-white"
            : "bg-background/78 backdrop-blur-sm border-b border-border/60 text-foreground"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
          <Logo size="md" inverted={transparent} />
        </Link>

        <nav className="hidden md:flex items-center gap-4 lg:gap-8">
          {visibleNav.map((n) =>
            n.hasDropdown ? (
              <div key={n.to} className="relative group">
                <div className="flex items-center gap-1">
                  <Link
                    to={n.to}
                    className="relative whitespace-nowrap text-[11px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.25em] opacity-80 hover:opacity-100 transition-opacity after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform after:duration-300 hover:after:scale-x-100"
                    activeProps={{ className: "opacity-100" }}
                  >
                    {n.label}
                  </Link>
                  <button
                    type="button"
                    aria-label={`${n.label} submenu`}
                    aria-expanded={openMenu === n.to}
                    onClick={() => setOpenMenu((cur) => (cur === n.to ? null : n.to))}
                    className="opacity-80 hover:opacity-100 transition-opacity p-1"
                  >
                    <ChevronDown size={12} className={`transition-transform group-hover:rotate-180 ${openMenu === n.to ? "rotate-180" : ""}`} />
                  </button>
                </div>
                <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-300 ${openMenu === n.to ? "opacity-100 visible" : "opacity-0 invisible group-hover:opacity-100 group-hover:visible"}`}>
                  <div className="bg-background/95 backdrop-blur-md border border-border min-w-[280px] py-2" onClick={() => setOpenMenu(null)}>
                    {n.isHome ? (
                      <>
                        <Link
                          to="/about"
                          className="block px-5 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
                        >
                          About Us
                        </Link>
                        <Link
                          to="/why-us"
                          className="block px-5 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
                        >
                          Why Us
                        </Link>
                        <Link
                          to="/blog"
                          className="block px-5 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
                        >
                          Blog
                        </Link>
                      </>
                    ) : n.isServices ? (
                      <>
                        <Link
                          to="/delivery"
                          className="block px-5 py-3 text-xs uppercase tracking-[0.2em] text-foreground hover:bg-accent transition-colors border-b border-border"
                        >
                          Delivery Overview
                        </Link>
                        {services.map((s) => (
                          <a
                            key={s.to}
                            href={s.to}
                            className="block px-5 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
                          >
                            {s.label}
                          </a>
                        ))}
                      </>
                    ) : (
                      <>
                        <Link
                          to="/fleet"
                          className="block px-5 py-3 text-xs uppercase tracking-[0.2em] text-foreground hover:bg-accent transition-colors border-b border-border"
                        >
                          View Entire Fleet
                        </Link>
                        {cars.map((c) => (
                          <Link
                            key={c.slug}
                            to="/fleet/$slug"
                            params={{ slug: c.slug }}
                            className="block px-5 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
                          >
                            {c.name}
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={n.to}
                to={n.to}
                className={`relative whitespace-nowrap text-[11px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.25em] opacity-80 hover:opacity-100 transition-opacity after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform after:duration-300 hover:after:scale-x-100 ${n.hideOnDesktop ? "lg:hidden" : ""}`}
                activeProps={{ className: "opacity-100" }}
              >
                {n.label}
              </Link>
            ),
          )}
        </nav>

        <div className="hidden md:flex shrink-0 items-center gap-3">
          <Link
            to="/contact"
            className={`hidden lg:inline-flex items-center leading-none whitespace-nowrap text-xs uppercase tracking-[0.25em] px-5 py-3 border transition-all duration-300 hover:tracking-[0.3em] ${
              transparent
                ? "border-white text-white hover:bg-white hover:text-foreground"
                : "border-foreground text-foreground hover:bg-foreground hover:text-background"
            }`}
          >
            Contact
          </Link>
          <a
            href="/book"
            className={`inline-flex items-center leading-none whitespace-nowrap text-[11px] lg:text-xs uppercase tracking-[0.15em] lg:tracking-[0.25em] px-4 py-3 lg:px-5 transition-all duration-300 hover:tracking-[0.3em] ${
              transparent
                ? "bg-white text-foreground hover:bg-white/90"
                : "bg-foreground text-background hover:bg-foreground/90"
            }`}
          >
            Book Now
          </a>
        </div>

        <button onClick={() => setOpen(!open)} className="md:hidden" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="px-6 py-6 flex flex-col gap-5">
            {visibleNav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.25em] text-foreground"
              >
                {n.label}
              </Link>
            ))}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <a
                href="tel:+17783798881"
                onClick={() => setOpen(false)}
                className="text-xs uppercase tracking-[0.25em] px-4 py-3 bg-foreground text-background text-center"
              >
                Call Us
              </a>
              <a
                href="/book"
                onClick={() => setOpen(false)}
                className="text-xs uppercase tracking-[0.25em] px-4 py-3 border border-foreground text-foreground text-center"
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
    </div>
  );
}
